import React, { FC, ReactNode } from 'react';
import { FetchMoreOptions } from 'apollo-client';

import { LoaderTypes } from 'types/app';
import {
  useGetContentEmailHistoryQuery,
  ContentEmailHistoryFragment,
  GetContentEmailHistoryDocument,
} from 'api';
import { QueryManager } from 'components';
import { CONTENT_EMAIL_LIMIT } from 'constants/index';

export interface IGetContentEmailHistoryResult {
  history: ContentEmailHistoryFragment[];
  loading: boolean;
  paginate: boolean;
  onLoadMore: () => void;
}

interface IProps {
  loader?: LoaderTypes;
  children: (result: IGetContentEmailHistoryResult) => ReactNode;
}

const GetContentEmailHistory: FC<IProps> = ({ children, loader }) => {
  const queryResult = useGetContentEmailHistoryQuery({
    notifyOnNetworkStatusChange: true,
    variables: { data: { limit: CONTENT_EMAIL_LIMIT } },
  });

  const { loading, data, error, fetchMore } = queryResult;

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, { fetchMoreResult }) => {
    if (!fetchMoreResult) {
      return prev;
    }

    const prevResult = prev.getContentEmailHistory;
    const nextResult = fetchMoreResult.getContentEmailHistory;

    return {
      getContentEmailHistory: {
        ...fetchMoreResult.getContentEmailHistory,
        history: [...prevResult.history, ...nextResult.history],
      },
    };
  };

  function handleLoadMore() {
    const { sent_at, spec_id, paginate } = data.getContentEmailHistory;

    if (loading || error || !paginate) {
      return Promise.resolve();
    }

    return fetchMore({
      query: GetContentEmailHistoryDocument,
      variables: { data: { sent_at, spec_id, limit: CONTENT_EMAIL_LIMIT } },
      updateQuery,
    });
  }

  return (
    <QueryManager loader={loader} {...queryResult}>
      {() => {
        if (!data?.getContentEmailHistory) {
          return null;
        }

        const { history, paginate } = data.getContentEmailHistory;

        return children({
          history,
          loading,
          paginate,
          onLoadMore: handleLoadMore,
        });
      }}
    </QueryManager>
  );
};

export default GetContentEmailHistory;
